import { DEALER_SEARCH_KEY } from '@/app/components/sections/dealer/utils/dealer-constant';
import type {
  ContactInfo,
  Dealer,
  DealerGrouping,
} from '@/services/contentstack/models/dealer';

export const isDealerCV = process.env.NEXT_PUBLIC_WEB === 'cv';

type DealerServiceType = 'sale' | 'aftersale' | 'bp';
type DealerCampaignType = 'campaign';

const getDealerStatus = (
  dealer: Dealer,
  key: DealerServiceType | DealerCampaignType
): boolean => {
  if (!dealer) return false;

  const suffix = isDealerCV ? 'cv' : 'lcv';

  if (key === 'campaign') return !!dealer.sale?.[`campaign_${suffix}`];

  return !!dealer[key]?.[`active_${suffix}`];
};

export const isDealerActiveSale = (dealer: Dealer) =>
  getDealerStatus(dealer, 'sale');

export const isDealerActiveAfterSale = (dealer: Dealer) =>
  getDealerStatus(dealer, 'aftersale');

export const isDealerActiveBodyPaint = (dealer: Dealer) =>
  getDealerStatus(dealer, 'bp');

export const isDealerRegisterable = (dealer: Dealer) =>
  getDealerStatus(dealer, 'campaign');

const toLink =
  (base: string, hostPrefix?: string | string[]) => (value?: string) => {
    if (!value?.trim()) return '';
    const trimmed = value.trim();
    if (trimmed.startsWith('http')) return trimmed;
    const prefixes = hostPrefix
      ? Array.isArray(hostPrefix)
        ? hostPrefix
        : [hostPrefix]
      : [];
    const hasHost = prefixes.some((p) =>
      trimmed.toLowerCase().startsWith(p.toLowerCase())
    );
    if (hasHost) return `https://${trimmed}`;
    return `${base}${trimmed.replace(/^\/+/, '')}`;
  };

const toFacebookLink = toLink('https://www.facebook.com/', [
  'www.facebook.com',
  'facebook.com',
]);
const toMessengerLink = toLink('https://m.me/', 'm.me');
export const toLineLink = toLink('https://line.me/ti/p/', 'line.me');

export const getDealerFacebook = (dealer: ContactInfo) => {
  return {
    cv: {
      name: dealer?.fb_cv_name || dealer?.fb_cv || '',
      link: toFacebookLink(dealer?.fb_cv),
    },
    lcv: {
      name: dealer?.fb_lcv_name || dealer?.fb_lcv || '',
      link: toFacebookLink(dealer?.fb_lcv),
    },
  };
};

export const getDealerMessenger = (dealer: ContactInfo) => {
  return {
    cv: {
      name: dealer?.messenger_cv_name || dealer?.messenger_cv || '',
      link: toMessengerLink(dealer?.messenger_cv),
    },
    lcv: {
      name: dealer?.messenger_lcv_name || dealer?.messenger_lcv || '',
      link: toMessengerLink(dealer?.messenger_lcv),
    },
  };
};

export const getDealerLine = (dealer: ContactInfo) => {
  return {
    cv: {
      name: dealer?.line_cv ?? '',
      link: toLineLink(dealer?.line_cv),
    },
    lcv: {
      name: dealer?.line_lcv ?? '',
      link: toLineLink(dealer?.line_lcv),
    },
  };
};

export const getDealerGoogleMapUrl = (
  dealer: {
    google_map_url?: string | null;
    lat?: string | null;
    lng?: string | null;
  },
  name?: string
) => {
  return (
    dealer?.google_map_url ||
    `https://www.google.com/maps/search/${name}/${dealer?.lat},${dealer?.lng}`
  );
};

export const groupingDealersForSearch = (dealers: DealerGrouping[]) => {
  if (dealers.length === 0) return [];

  const result: DealerGrouping[] = [];

  for (const dealer of dealers) {
    if (isDealerActiveSale(dealer)) {
      result.push({
        ...dealer,
        groupForSearch: {
          id: `${dealer.uid}-${DEALER_SEARCH_KEY.Showroom}`,
          type: DEALER_SEARCH_KEY.Showroom,
          name: dealer?.sale?.name_th || '',
          address: dealer?.sale?.address || '',
        },
      });
    }

    if (isDealerActiveAfterSale(dealer)) {
      result.push({
        ...dealer,
        groupForSearch: {
          id: `${dealer.uid}-${DEALER_SEARCH_KEY.Service}`,
          type: DEALER_SEARCH_KEY.Service,
          name: dealer?.aftersale?.name_th || '',
          address: dealer?.aftersale?.address || '',
        },
      });
    }

    if (isDealerActiveBodyPaint(dealer)) {
      result.push({
        ...dealer,
        groupForSearch: {
          id: `${dealer.uid}-${DEALER_SEARCH_KEY.BodyPaint}`,
          type: DEALER_SEARCH_KEY.BodyPaint,
          name: dealer?.bp?.name_th || '',
          address: dealer?.bp?.address || '',
        },
      });
    }
  }

  return result;
};
